import React from 'react'
import { X, AlertTriangle, Clock, Users, DollarSign } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAlerts } from '@/contexts/AlertContext'
import { cn } from '@/lib/utils'

export const AlertBannerSystem: React.FC = () => {
  const { activeAlerts, dismissAlert, acknowledgeAlert } = useAlerts()

  // Show only the most critical alert
  const criticalAlert = activeAlerts.find(alert => alert.severity === 'critical')
  const highAlert = activeAlerts.find(alert => alert.severity === 'high')
  const displayAlert = criticalAlert || highAlert

  if (!displayAlert) return null

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-gradient-alert text-white border-risk-critical animate-alert-pulse'
      case 'high':
        return 'bg-risk-high text-white border-risk-high'
      case 'medium':
        return 'bg-risk-medium text-white border-risk-medium'
      default:
        return 'bg-primary text-primary-foreground border-primary'
    }
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'sequential':
        return '📱'
      case 'velocity':
        return '⚡'
      case 'datacenter':
        return '🖥️'
      case 'domain':
        return '📧'
      case 'zip':
        return '📍'
      default:
        return '🚨'
    }
  }

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date()
    const diff = now.getTime() - timestamp.getTime()
    const minutes = Math.floor(diff / 60000)
    
    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return timestamp.toLocaleDateString()
  }

  const estimatedSavings = displayAlert.affectedLeads * 30 // $30 per blocked fraudulent lead

  return (
    <div className={cn(
      "fixed top-0 left-0 right-0 z-50 border-b-2 transition-all duration-300",
      getSeverityStyles(displayAlert.severity)
    )}>
      <div className="flex items-center justify-between max-w-7xl mx-auto p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-2xl animate-pulse">
              {getIcon(displayAlert.type)}
            </span>
            <AlertTriangle className="h-5 w-5 animate-pulse" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold uppercase text-sm tracking-wide">
                {displayAlert.severity} ALERT
              </span>
              <div className="h-1 w-1 bg-current rounded-full animate-pulse" />
              <span className="text-xs opacity-90">
                {displayAlert.type.toUpperCase()}
              </span>
            </div>
            
            <h2 className="font-semibold text-lg mb-1">
              {displayAlert.title}
            </h2>
            
            <p className="text-sm opacity-90 max-w-2xl">
              {displayAlert.message}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2 bg-black/20 px-3 py-2 rounded-full">
              <Clock className="h-4 w-4" />
              <span>{formatTimestamp(displayAlert.timestamp)}</span>
            </div>
            
            <div className="flex items-center gap-2 bg-black/20 px-3 py-2 rounded-full">
              <Users className="h-4 w-4" />
              <span>{displayAlert.affectedLeads} leads</span>
            </div>
            
            {estimatedSavings > 0 && (
              <div className="flex items-center gap-2 bg-black/20 px-3 py-2 rounded-full">
                <DollarSign className="h-4 w-4" />
                <span>${estimatedSavings} saved</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {displayAlert.actions?.map((action) => (
              <Button
                key={action.id}
                variant={action.type === 'destructive' ? 'destructive' : 'secondary'}
                size="sm"
                onClick={() => {
                  action.handler()
                  acknowledgeAlert(displayAlert.id)
                }}
                className="text-xs h-8 px-3 bg-white/20 hover:bg-white/30 text-current border-white/30"
              >
                {action.label}
              </Button>
            ))}
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => acknowledgeAlert(displayAlert.id)}
              className="text-current hover:bg-white/20 h-8 w-8 p-0"
              title="Acknowledge alert"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Progress bar for auto-dismiss */}
      {displayAlert.autoDismiss && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
          <div 
            className="h-full bg-white/50 transition-all duration-1000 ease-linear"
            style={{ 
              animation: `shrink ${displayAlert.autoDismiss}ms linear forwards` 
            }}
          />
        </div>
      )}
    </div>
  )
}

// Add CSS animation for progress bar
const style = document.createElement('style')
style.textContent = `
  @keyframes shrink {
    from { width: 100%; }
    to { width: 0%; }
  }
`
document.head.appendChild(style)